import Box from '@components/base/Box'
import React, { useEffect, useMemo, useState } from 'react'
import { BaseInputProps } from './BaseInputProps'
import { validate } from './Validations'

export interface PriceInputProps extends BaseInputProps {
  currencySymbol?: string | '£' | '$' | '€' | '¥' | '₹'
  minValue?: number
  maxValue?: number
  value?: number
}

/**
 * Primary UI component for count inputs.
 */
export const CountInput = ({
  label,
  value = 0,
  onChange,
  className,
  validations,
  required,
  textColour,
  border = 'border',
  show = true,
  minValue = 0,
  maxValue = 99,
  error,
}: PriceInputProps) => {
  const errorMessage = error ?? validate(value, validations)
  const [count, setCount] = useState<number>(value)

  const inputId = useMemo(
    () =>
      label
        ? `count-input-${label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-')}`
        : undefined,
    [label]
  )

  const clampValue = (nextValue: number) => {
    if (!Number.isFinite(nextValue)) return minValue
    return Math.min(maxValue, Math.max(minValue, nextValue))
  }

  const updateCount = (nextValue: number) => {
    const nextCount = clampValue(nextValue)
    setCount(nextCount)
    onChange && onChange(nextCount)
  }

  useEffect(() => {
    setCount(clampValue(value))
  }, [value, minValue, maxValue])

  if (!show) return <></>

  const borderStyles =
    border === 'border-none'
      ? 'border-none'
      : `${border} ${
          errorMessage
            ? 'border-red-500 focus-within:border-red-500'
            : 'border-gray-300 focus-within:border-forumm-blue'
        }`

  const buttonClassName =
    'flex h-11 w-11 items-center justify-center border-gray-300 text-lg font-semibold text-[#153e3e] transition-all hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#153e3e]/30 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent'

  return (
    <Box
      ignoreTheme
      className={['flex flex-col my-3', className].join(' ')}
      style={{ color: textColour }}
    >
      {label && (
        <label
          htmlFor={inputId}
          className={`mb-1.5 text-sm font-medium leading-5 text-[#153e3e] transition-all ${
            errorMessage ? 'text-red-500' : ''
          } ${
            required ? "after:content-['*'] after:ml-1 after:text-red-500" : ''
          }`}
        >
          {label}
        </label>
      )}

      <Box
        ignoreTheme
        className={`flex h-11 w-fit overflow-hidden rounded bg-white/70 transition-all ${borderStyles}`}
      >
        <button
          type="button"
          aria-label={`Decrease ${label ?? 'value'}`}
          className={`${buttonClassName} border-r`}
          disabled={count <= minValue}
          onClick={() => {
            updateCount(count - 1)
          }}
        >
          -
        </button>
        <input
          id={inputId}
          required={required}
          value={Number.isNaN(count) ? '' : count}
          onChange={(e) => {
            updateCount(parseInt(e.target.value, 10))
          }}
          min={minValue}
          max={maxValue}
          type="number"
          className={`h-11 w-14 bg-transparent px-2 py-2 text-center text-sm font-medium text-[#153e3e] outline-none transition-all placeholder-gray-500 focus:ring-0 ${
            errorMessage ? 'text-red-500' : ''
          }`}
        />
        <button
          type="button"
          aria-label={`Increase ${label ?? 'value'}`}
          className={`${buttonClassName} border-l`}
          disabled={count >= maxValue}
          onClick={() => {
            updateCount(count + 1)
          }}
        >
          +
        </button>
      </Box>

      {errorMessage && (
        <Box ignoreTheme className="mt-1 text-sm font-bold text-red-500">
          {errorMessage}
        </Box>
      )}
    </Box>
  )
}
